import React from 'react'
import ReactDOM from 'react-dom/client'
import AuthGate from './AuthGate.jsx'
import CupperMobile from './CupperMobile.jsx'
import SampleView from './SampleView.jsx'
import ReceiveView from './ReceiveView.jsx'

// Cupper-invite links (/cup/<token>) render a bare-chrome scoring
// page — no auth, the token is the auth.
const cupMatch = window.location.pathname.match(/^\/cup\/([A-Za-z0-9_-]{8,})\/?$/);
const cupToken = cupMatch ? cupMatch[1] : null;
// PO receiving page (/receive/<poId>) — same pattern. The PO id is
// the auth, and the page is mobile/iPad-friendly for warehouse use.
const receiveMatch = window.location.pathname.match(/^\/receive\/([A-Za-z0-9_-]+)\/?$/);
const receivePoId = receiveMatch ? receiveMatch[1] : null;
// Sample QR-code labels link here. Public read-only sample detail.
// Two URL forms are supported:
//   /s/<6charCode>                — modern short code (what QR labels
//                                   actually encode; keeps the QR tiny)
//   /s/<sessionId>/<sampleId>     — legacy long form for older labels
const longSampleMatch = window.location.pathname.match(/^\/s\/([A-Za-z0-9_-]+)\/([A-Za-z0-9_-]+)\/?$/i);
const shortSampleMatch = window.location.pathname.match(/^\/s\/([A-Z0-9]{6})\/?$/i);
const sampleRoute = longSampleMatch
  ? { sessionId: longSampleMatch[1], sampleId: longSampleMatch[2] }
  : shortSampleMatch
    ? { code: shortSampleMatch[1].toUpperCase() }
    : null;

/* ── ErrorBoundary — catches any render exception and replaces the
   blank white screen with an actionable message + reload button.
   Without this, any uncaught render error silently unmounts the
   whole app, leaving the user staring at a blank page. ── */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("[Elion Roaster] Render error:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          minHeight: "100vh", display: "flex", alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #0E2A33 0%, #050A0D 60%, #000 100%)",
          fontFamily: "Inter, sans-serif", padding: 24,
          color: "#fff",
          position: "relative", overflow: "hidden",
        }}>
          {/* Soft animated cyan glow behind the headline so the page
              feels alive instead of broken. CSS-only so we don't add
              a runtime dep just for one screen. */}
          <style>{`
            @keyframes pr-pulse { 0%,100% { opacity: 0.55; transform: scale(1) } 50% { opacity: 1; transform: scale(1.06) } }
            @keyframes pr-rise  { 0% { opacity: 0; transform: translateY(8px) } 100% { opacity: 1; transform: translateY(0) } }
            @keyframes pr-shimmer { 0% { background-position: -120% 0 } 100% { background-position: 220% 0 } }
          `}</style>
          <div style={{
            position: "absolute", top: "32%", left: "50%",
            width: 520, height: 520, marginLeft: -260, marginTop: -260,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(34,211,238,0.28) 0%, rgba(34,211,238,0.05) 45%, transparent 70%)",
            filter: "blur(40px)",
            animation: "pr-pulse 4.5s ease-in-out infinite",
            pointerEvents: "none",
          }} />
          <div style={{
            maxWidth: 520, textAlign: "center", position: "relative", zIndex: 1,
            animation: "pr-rise 0.55s ease-out both",
          }}>
            {/* Eyebrow */}
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#22D3EE",
              letterSpacing: "0.18em", textTransform: "uppercase",
              marginBottom: 18,
            }}>
              <span style={{
                display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                background: "#22D3EE", marginRight: 8,
                boxShadow: "0 0 12px #22D3EE",
                animation: "pr-pulse 1.6s ease-in-out infinite",
              }} />
              Build in progress
            </div>
            {/* Headline */}
            <h1 style={{
              fontSize: 40, fontWeight: 800, letterSpacing: "-0.02em",
              margin: "0 0 14px", lineHeight: 1.05,
              background: "linear-gradient(90deg, #fff 0%, #22D3EE 45%, #fff 100%)",
              backgroundSize: "200% 100%",
              WebkitBackgroundClip: "text", backgroundClip: "text",
              WebkitTextFillColor: "transparent", color: "transparent",
              animation: "pr-shimmer 6s linear infinite",
            }}>
              We're currently making an update!
            </h1>
            <p style={{
              fontSize: 15, color: "rgba(255,255,255,0.72)",
              margin: "0 0 28px", lineHeight: 1.6, maxWidth: 440,
              marginLeft: "auto", marginRight: "auto",
            }}>
              The roastery's changing rooms while you're standing in it. Give it a beat,
              reload, and pour another cup. If it doesn't bounce back, let us know at{" "}
              <a href="mailto:hello@elionlabs.org" style={{ color: "#22D3EE", textDecoration: "none", fontWeight: 600 }}>
                hello@elionlabs.org
              </a>.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "13px 28px", borderRadius: 99, border: "none",
                background: "linear-gradient(135deg, #22D3EE 0%, #06B6D4 100%)",
                color: "#001619", fontSize: 14, fontWeight: 700,
                cursor: "pointer", fontFamily: "Inter, sans-serif",
                letterSpacing: "0.01em",
                boxShadow: "0 12px 32px rgba(34,211,238,0.35), inset 0 1px 0 rgba(255,255,255,0.4)",
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 16px 40px rgba(34,211,238,0.45), inset 0 1px 0 rgba(255,255,255,0.4)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(34,211,238,0.35), inset 0 1px 0 rgba(255,255,255,0.4)"; }}
            >
              Reload the app →
            </button>
            {/* Quietly stash the technical bits — they're useful when
                something IS broken, but we don't want them shouting
                during a routine deploy. */}
            <details style={{
              maxWidth: 440, margin: "26px auto 0", textAlign: "left",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 10, padding: "10px 14px",
              fontSize: 12, color: "rgba(255,255,255,0.6)",
            }}>
              <summary style={{ cursor: "pointer", fontWeight: 500, listStyle: "none" }}>
                Technical details
              </summary>
              <pre style={{
                marginTop: 8, whiteSpace: "pre-wrap", wordBreak: "break-all",
                color: "#FCA5A5", fontSize: 11, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}>
                {this.state.error?.message}
              </pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      {cupToken ? <CupperMobile token={cupToken} />
        : sampleRoute ? <SampleView {...sampleRoute} />
        : receivePoId ? <ReceiveView poId={receivePoId} />
        : <AuthGate />}
    </ErrorBoundary>
  </React.StrictMode>,
)
